import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  Plus,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileEdit,
  Archive,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { useControlledDocuments, type DocumentFilters } from '@/hooks/use-documents';
import type { ControlledDocument, DocumentCategory, DocumentStatus } from '@/lib/types';
import { formatDateOnly } from '@/lib/date-utils';

const STATUS_CONFIG: Record<DocumentStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle2 }> = {
  draft: { label: 'Draft', variant: 'secondary', icon: FileEdit },
  under_review: { label: 'Awaiting approval', variant: 'outline', icon: Clock },
  approved: { label: 'Approved', variant: 'default', icon: CheckCircle2 },
  superseded: { label: 'Superseded', variant: 'secondary', icon: Archive },
  archived: { label: 'Archived', variant: 'secondary', icon: Archive },
};

const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  policy: 'Policy',
  procedure: 'Procedure',
  form: 'Form',
  template: 'Template',
  record: 'Record',
  manual: 'Manual',
  other: 'Other',
};

function StatusBadge({ status }: { status: DocumentStatus }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  return (
    <Badge variant={config.variant} className="flex items-center gap-1">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function DocumentRow({ doc }: { doc: ControlledDocument }) {
  const isOverdue = doc.is_overdue;

  return (
    <Link
      to={`/compliance/documents/${doc.id}`}
      className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-400 shrink-0" />
            <h3 className="font-medium text-sm truncate">{doc.title}</h3>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
            {doc.document_number && <span className="font-mono">{doc.document_number}</span>}
            <span>{CATEGORY_LABELS[doc.category]}</span>
            <span>v{doc.current_version}</span>
            <span>Owner: {doc.owner_username}</span>
          </div>
          {doc.description && (
            <p className="text-xs text-gray-400 mt-1 truncate">{doc.description}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <StatusBadge status={doc.status} />
          {isOverdue && (
            <Badge variant="destructive" className="flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Review Overdue
            </Badge>
          )}
          {doc.next_review_due && (
            <span className="text-xs text-gray-400">
              Review: {formatDateOnly(doc.next_review_due)}
            </span>
          )}
          {doc.requires_acknowledgment && (
            <span className="text-xs text-gray-400">
              {doc.acknowledgment_count} acknowledged
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export function DocumentsPage() {
  const [filters, setFilters] = useState<DocumentFilters>({
    page: 1,
    page_size: 25,
  });
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useControlledDocuments(filters);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput, page: 1 }));
  };

  const documents = data?.results || [];
  const totalCount = data?.count || 0;

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Failed to load documents. Please try again later.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Document Register</h1>
          <p className="text-gray-600 mt-1">
            Version-controlled documents with approval workflows (QP-300 Clause 2.8)
          </p>
        </div>
        <Link to="/compliance/documents/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Document
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search documents..."
                  className="pl-9"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <Button type="submit" variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
            </form>
            <Select
              value={filters.category || 'all'}
              onValueChange={(v) =>
                setFilters((prev) => ({ ...prev, category: v === 'all' ? undefined : v, page: 1 }))
              }
            >
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.status || 'all'}
              onValueChange={(v) =>
                setFilters((prev) => ({ ...prev, status: v === 'all' ? undefined : v, page: 1 }))
              }
            >
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Documents</span>
            <span className="text-sm font-normal text-gray-500">{totalCount} total</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No documents found</p>
              <Link to="/compliance/documents/new" className="mt-3 inline-block">
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Upload First Document
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map((doc) => (
                <DocumentRow key={doc.id} doc={doc} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalCount > (filters.page_size || 25) && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                disabled={!data?.previous}
                onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-500">
                Page {filters.page || 1} of {Math.ceil(totalCount / (filters.page_size || 25))}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!data?.next}
                onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
